import React, { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Pencil, Download } from 'lucide-react';
import { PresupuestoPDF } from '@/components/PresupuestoPDF';
import { usePresupuestos, useConfig } from '@/hooks/useData';
import { sanitizePdfHtml } from '@/lib/security';

export default function PreviewPresupuesto() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lista } = usePresupuestos();
  const { config } = useConfig();
  const pdfRef = useRef<HTMLDivElement>(null);
  const presupuesto = lista.find(p => p.id === id);

  const handleDownloadPDF = () => {
    const node = pdfRef.current;
    if (!node) return;

    // Capturamos el HTML renderizado con todos los estilos inline
    // y lo sanitizamos para prevenir inyección XSS en la ventana de impresión
    const htmlContent = sanitizePdfHtml(node.innerHTML);

    const win = window.open('', '_blank', 'width=900,height=1200');
    if (!win) {
      alert('Tu navegador bloqueó la ventana emergente. Permite las ventanas emergentes para este sitio e inténtalo de nuevo.');
      return;
    }

    win.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>${presupuesto?.numero ?? 'Presupuesto'}</title>
  <link
    href="https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=IBM+Plex+Mono:wght@400;500;600&display=swap"
    rel="stylesheet"
  />
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body {
      background: #ffffff;
      font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    @page {
      size: A4 portrait;
      margin: 0;
    }
    @media print {
      html, body { width: 210mm; min-height: 297mm; }
      .no-print { display: none !important; }
    }
    /* Botón de impresión — solo visible en pantalla */
    .print-bar {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: 12px 20px;
      background: #1e3a5f;
      gap: 10px;
    }
    .print-bar button {
      background: #ffffff;
      color: #1e3a5f;
      border: none;
      padding: 8px 20px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
    }
    .print-bar button:hover { background: #e8f0fe; }
    .print-bar span { color: #a0b4cc; font-size: 12px; }
    @media print { .print-bar { display: none !important; } }
  </style>
</head>
<body>
  <div class="print-bar no-print">
    <span>Selecciona "Guardar como PDF" → activa "Gráficos de fondo"</span>
    <button onclick="window.print()">🖨️ Guardar como PDF</button>
  </div>
  <div style="background:#ffffff;margin:0;padding:0;">
    ${htmlContent}
  </div>
</body>
</html>`);

    win.document.close();
    // Pequeña pausa para que se carguen las fuentes de Google antes de imprimir
    win.addEventListener('load', () => {
      setTimeout(() => win.focus(), 300);
    });
  };

  if (!presupuesto) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Presupuesto no encontrado.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-200">
      {/* ── Barra de acciones ── */}
      <div className="sticky top-0 z-30 bg-card border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="h-9 w-9">
              <ArrowLeft size={18} />
            </Button>
            <span className="font-mono text-sm font-semibold text-primary">{presupuesto.numero}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/editor/${presupuesto.id}`)}
              className="gap-1.5"
            >
              <Pencil size={15} /> Editar
            </Button>
            <Button size="sm" onClick={handleDownloadPDF} className="gap-1.5">
              <Download size={15} /> Descargar PDF
            </Button>
          </div>
        </div>
      </div>

      {/* ── Banner de instrucción ── */}
      <div className="bg-blue-50 border-b border-blue-200 py-2 px-4 text-center text-xs text-blue-700">
        💡 Pulsa <strong>Descargar PDF</strong> → se abre una nueva ventana → haz clic en <strong>"Guardar como PDF"</strong> y activa <strong>"Gráficos de fondo"</strong> en las opciones de impresión.
      </div>

      {/* ── Vista previa en pantalla ── */}
      <div className="py-8 px-4">
        {/* Contenedor con sombra de papel */}
        <div
          style={{
            boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
            borderRadius: '2px',
            maxWidth: '210mm',
            margin: '0 auto',
            background: '#ffffff',
            overflow: 'hidden',
          }}
        >
          {/* Este div es la fuente del HTML que se inyecta en la ventana de impresión */}
          <div ref={pdfRef}>
            <PresupuestoPDF presupuesto={presupuesto} config={config} />
          </div>
        </div>
      </div>
    </div>
  );
}
